import { Button, Box, Typography, CircularProgress } from '@mui/material'
import React from 'react'

interface OfficeFormSubmitButtonProps {
  isSubmitting: boolean
  isRetrying?: boolean
  retryCount?: number
  isEditMode?: boolean
}

/**
 * OfficeFormSubmitButton - Component for office form submission button
 *
 * Handles the submit button with appropriate text and disabled state for office creation/editing.
 */
export default function OfficeFormSubmitButton({
  isSubmitting,
  isRetrying = false,
  retryCount = 0,
  isEditMode = false
}: OfficeFormSubmitButtonProps) {
  const getButtonText = () => {
    if (isRetrying && retryCount > 0) {
      return `Retrying... (${retryCount}/3)`
    }
    if (isSubmitting) {
      return isEditMode ? 'Updating Office...' : 'Creating Office...'
    }
    return isEditMode ? 'Update Office' : 'Create Office'
  }

  const isDisabled = isSubmitting || isRetrying

  return (
    <Box>
      <Button
        type='submit'
        fullWidth
        variant='contained'
        sx={{
          mt: 4,
          mb: isRetrying ? 1 : 2,
          py: 1.5,
          borderRadius: 2,
          background: isDisabled
            ? 'rgba(0,0,0,0.12)'
            : 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
          boxShadow: isDisabled
            ? 'none'
            : '0 4px 12px rgba(102, 126, 234, 0.4)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: isDisabled ? 'none' : 'translateY(-2px)',
            boxShadow: isDisabled
              ? 'none'
              : '0 8px 24px rgba(102, 126, 234, 0.6)'
          },
          '&:disabled': {
            background: 'rgba(0,0,0,0.12)',
            transform: 'none',
            boxShadow: 'none'
          }
        }}
        disabled={isDisabled}
        color='primary'
        id={isEditMode ? 'updateoffice' : 'createoffice'}
        data-testid={
          isEditMode
            ? 'update-office-submit-button'
            : 'create-office-submit-button'
        }
        startIcon={
          isSubmitting || isRetrying ? (
            <CircularProgress size={20} color='inherit' />
          ) : undefined
        }
      >
        {getButtonText()}
      </Button>

      {/* Retry feedback */}
      {isRetrying && (
        <Typography
          variant='body2'
          color='text.secondary'
          sx={{
            textAlign: 'center',
            mb: 2,
            fontStyle: 'italic'
          }}
        >
          Connection issues detected. Retrying automatically...
        </Typography>
      )}
    </Box>
  )
}
